import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import { FC, useRef } from 'react'
import { Form as FinalForm } from 'react-final-form'
import Dialog from '../../../../control/dialog/dialog'
import useLoading from '../../../../control/loading/useLoading'
import MyTextfield from '../../../../control/textfield/textfield'
import useTranslate from '../../../../i18n/useTranslate'
import { ItemListType } from '../../../../services/web/types/responseWebServiceTypes'
import useWebService from '../../../../services/web/useWebService'
import useCheckPermission from '../../../../utilities/checkPermission'
import DeleteIcon from '@material-ui/icons/Delete'
import { styled } from '@material-ui/core'
import { ButtonView } from '../../../../control/button/button'

const CloseButton = styled(Button)({
    border: '1px solid #DB5F8E',
    boxShadow: '0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)',
})

const DialogDeleteProject: FC<{ item: ItemListType; pendingProps: any }> = ({ item, pendingProps }) => {
    const { deleteProjectRequest } = useWebService()
    const translate = useTranslate()
    const dialogRef = useRef<Record<string, Function>>()
    const successRef = useRef<Record<string, Function>>()
    const failRef = useRef<Record<string, Function>>()
    const loadingHook = useLoading()
    const checkPermissionHook = useCheckPermission()
    const remarkRef = useRef<string>('')

    const bindingDialogRef = (func: Record<string, Function>) => {
        dialogRef.current = { ...func }
    }
    const handleCloseDialog = () => dialogRef?.current?.closeDialog()
    const handleOpenDialog = () => dialogRef?.current?.openDialog()

    const onSetSuccessRef = (func: Record<string, Function>) => (successRef.current = { ...func })
    const onSetFailRef = (func: Record<string, Function>) => (failRef.current = { ...func })

    const validateForm = (values: any) => {
        const errors: any = {}
        if (!values.remark || `${values.remark}`.trim() === '') {
            errors.remark = translate.get('MESSAGE.M023')
        }
        return errors
    }

    const callDeleteProject = async () => {
        loadingHook.loadingOpen()
        try {
            const res = await deleteProjectRequest(item.projectID, {
                remark: remarkRef.current ?? null,
            })
            if (res?.status !== 1) {
                failRef.current?.openDialog()
                return
            }
            handleCloseDialog()
            successRef.current?.openDialog()
        } catch {
            failRef.current?.openDialog()
        } finally {
            loadingHook.loadingClose()
        }
    }

    const afterSubmit = () => {
        const param = pendingProps?.searchParamRef?.current
        if (param) pendingProps.afterSubmitPending?.(param)
    }

    return (
        <>
            <Button
                variant="contained"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={handleOpenDialog}
                style={{ visibility: checkPermissionHook.isUserAdmin() ? 'visible' : 'hidden' }}
            >
                {translate.get('BUTTON.DELETE_RESEARCH_PROJECT')}
            </Button>

            <Dialog
                maxWidth="xs"
                onRef={bindingDialogRef}
                type=""
                title={translate.get('LABEL.DELETE_REASON')}
                content={
                    <FinalForm onSubmit={callDeleteProject} initialValues={{ remark: '' }} validate={validateForm}>
                        {({ handleSubmit, values }) => {
                            remarkRef.current = values?.remark || ''
                            return (
                                <>
                                    <MyTextfield
                                        name="remark"
                                        materialInputProps={{
                                            label: translate.get('LABEL.PLEASE_SPECIFY_REMARK'),
                                            disabled: false,
                                            placeholder: translate.get('LABEL.PLEASE_SPECIFY_REMARK'),
                                            size: 'medium',
                                            rows: 5,
                                            multiline: true,
                                        }}
                                        required
                                    />
                                    <pre />
                                    <Grid container justify="center" alignItems="center" spacing={1}>
                                        <Grid item>
                                            <CloseButton onClick={handleCloseDialog}>
                                                {translate.get('BUTTON.CANCEL')}
                                            </CloseButton>
                                        </Grid>
                                        <Grid item>
                                            <ButtonView color="primary" type="submit" onClick={handleSubmit}>
                                                {translate.get('BUTTON.SAVE')}
                                            </ButtonView>
                                        </Grid>
                                    </Grid>
                                </>
                            )
                        }}
                    </FinalForm>
                }
            />

            <Dialog
                onRef={onSetSuccessRef}
                type="alert-message"
                title={translate.get('ALERT.TITLE.NOTIFICATION')}
                content={translate.get('ALERT.CONTENT.DELETE_SUCCESS') || translate.get('ALERT.CONTENT.SAVE_SUCCESS')}
                onAfterClose={afterSubmit}
            />
            <Dialog
                onRef={onSetFailRef}
                type="alert-message"
                title={translate.get('ALERT.TITLE.NOTIFICATION')}
                content={
                    translate.get('ALERT.CONTENT.DELETE_NOT_SUCCESS') || translate.get('ALERT.CONTENT.SAVE_NOT_SUCCESS')
                }
            />
        </>
    )
}

export default DialogDeleteProject
